import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Bold,
  Calendar as CalendarIcon,
  Folder,
  Inbox,
  Italic,
  Paperclip,
  Strikethrough,
  Tag,
  Underline,
  Upload,
  UserRound,
  X,
} from 'lucide-react'
import { TbAntennaBars5 } from 'react-icons/tb'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { getDueDateDisplayInfo } from '../utils/datetime'
import { detectTrailingDate } from '../utils/parseNaturalDate'
import { createChipClass } from '../utils/createChipClasses'
import { PriorityComboBox, PriorityGlyph } from './PriorityComboBox'
import { StatusComboBox } from './StatusComboBox'
import { ProjectComboBox } from './ProjectComboBox'
import { AssigneeComboBox } from './AssigneeComboBox'
import { DateDurationPicker } from './DateDurationPicker'
import type { KeyboardEvent, ReactNode } from 'react'
import type { Editor } from '@tiptap/core'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { LabelDocument } from '@/lib/db/rxdb/schemas/label'
import type { LabelGroupDocument } from '@/lib/db/rxdb/schemas/labelGroup'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import type { RecurrencePattern } from '@/lib/types/recurrence'
import type { FloatingFormatToolbarItem } from '@/components/editor/FloatingToolbar'
import type { StatusMode } from '@/hooks/useProjectStatusMode'
import type { NaturalDateMatch } from '../utils/parseNaturalDate'
import { useIsMobile } from '@/hooks/useMobile'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { TaskLabelsDisplay } from '@/components/features/labels/components'
import { ResponsivePopover } from '@/components/ui/responsive-popover'
import {
  AttachmentPicker,
  FILE_INPUT_ACCEPT,
  validateFiles,
} from '@/components/features/attachments'
import { Kbd } from '@/components/ui/kbd'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { TiptapRichTextEditor } from '@/components/editor/TiptapRichTextEditor'
import { LabelSelectionPanel } from '@/components/features/labels/components/LabelSelectionPanel'
import { PICKER_SIDE_OFFSET, pickerPopoverClassName } from '@/components/app/combo-box/PickerPanel'
import { getPlatformShortcutKeys } from '@/lib/utils/keyboard'
import { useLocale } from '@/lib/i18n/LocaleProvider'
import { useCurrentSubscription, useUsageLimitCheck } from '@/lib/db/rxdb/hooks/entities/subscriptions'
import { useHasPermission } from '@/lib/hooks/useWorkspaceRole'
import { WORKSPACE_PERMISSIONS } from '@/lib/types/roles'
import { WorkItemTypePicker } from '@/components/app/tasks/WorkItemTypePicker'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'

const DESCRIPTION_TOOLBAR_ITEMS: Array<FloatingFormatToolbarItem> = [
  { format: 'bold', label: 'Bold', Icon: Bold },
  { format: 'italic', label: 'Italic', Icon: Italic },
  { format: 'underline', label: 'Underline', Icon: Underline },
  { format: 'strikethrough', label: 'Strike', Icon: Strikethrough },
]

export interface AddTaskInputProps {
  // Input handlers
  onNameChange?: (value: string) => void
  onDescriptionChange?: (value: string) => void
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
  onCreateClick?: () => void
  /** Row variant: Shift+Enter adds the task and opens it. */
  onCreateAndOpenClick?: () => void
  onCancelClick?: () => void
  /**
   * Pasting two or more lines into the title makes one task per line
   * (design 33a: "Paste a list"). Unset, a paste is just text.
   */
  onPasteLines?: (lines: Array<string>) => void

  // Add more toggle
  addMore?: boolean
  onAddMoreChange?: (value: boolean) => void

  // Values
  nameValue?: string
  descriptionValue?: string

  // Controls
  startDate?: Date
  dueDate?: Date
  onStartDateChange?: (date: Date | undefined) => void
  onDueDateChange?: (date: Date | undefined) => void
  isAllDay?: boolean
  onIsAllDayChange?: (isAllDay: boolean) => void

  selectedPriority?: number
  onPriorityChange?: (value: number) => void

  selectedStatusId?: string | null
  statuses?: Array<StatusDocument>
  onStatusChange?: (value: string | null) => void

  selectedProjectId?: string | null
  projects?: Array<ProjectDocument>
  onProjectChange?: (value: string | null) => void
  disableProjectSelection?: boolean

  selectedUserId?: string | null
  onUserChange?: (value: string | null) => void

  selectedLabelIds?: Array<string>
  availableLabels?: Array<LabelDocument>
  labelGroups?: Array<LabelGroupDocument>
  onLabelsChange?: (labelIds: Array<string>) => void

  selectedFiles?: Array<File>
  onFilesChange?: (files: Array<File>) => void

  selectedWorkItemTypeId?: string | null
  onWorkItemTypeChange?: (typeId: string) => void
  /** Number of work item types available for this project. Picker renders when >= 1 and `onWorkItemTypeChange` is provided. */
  workItemTypeCount?: number

  isSomeday?: boolean
  onSomedayChange?: (isSomeday: boolean) => void

  reminderOffsetMinutes?: number | null
  onReminderChange?: (value: number | null) => void

  recurrencePattern?: RecurrencePattern | null
  onRecurrenceChange?: (pattern: RecurrencePattern | null) => void

  /**
   * When true (default), trailing natural-language date phrases in the title
   * (e.g. "...by today", "...on Friday") are auto-detected, stripped from the
   * title, and applied to `onDueDateChange`. Requires `onDueDateChange` to be
   * provided; otherwise this is a no-op.
   */
  enableNaturalLanguageDate?: boolean

  /** Row variant: how many typed tokens became properties ("N parsed"). */
  parsedCount?: number
  /**
   * Row variant: the project the current view files tasks under. The project
   * shows as a chip only once it differs from this.
   */
  defaultProjectId?: string | null
  /** Modal variant: more chips after Labels (the dialog's Cycle chip). */
  extraChips?: ReactNode


  // Layout options
  // inline = one-line style, full = expanded with create/cancel buttons,
  // row = quick add shaped like the task it will become (design 25a),
  // modal = the global create dialog's body (design 29a)
  variant?: 'inline' | 'full' | 'row' | 'modal'
  showDescription?: boolean
  showProject?: boolean
  outlined?: boolean
  placeholder?: string
  descriptionPlaceholder?: string
  icon?: ReactNode | null // null to hide icon completely

  // Status mode
  statusMode?: StatusMode

  // Class names
  className?: string
  inputClassName?: string
  controlsClassName?: string
}

export const AddTaskInput = forwardRef<HTMLInputElement, AddTaskInputProps>(
  (
    {
      // Handlers
      onNameChange,
      onPasteLines,
      onDescriptionChange,
      onKeyDown,
      onCreateClick,
      onCreateAndOpenClick,
      onCancelClick,

      // Add more toggle
      addMore = false,
      onAddMoreChange,

      // Values
      nameValue,
      descriptionValue,

      // Controls
      startDate,
      dueDate,
      onStartDateChange,
      onDueDateChange,
      isAllDay,
      onIsAllDayChange,
      selectedPriority = 0,
      onPriorityChange,
      selectedStatusId = null,
      statuses = [],
      onStatusChange,
      selectedProjectId = null,
      projects = [],
      onProjectChange,
      disableProjectSelection = false,
      selectedUserId = null,
      onUserChange,
      selectedLabelIds = [],
      availableLabels = [],
      labelGroups = [],
      onLabelsChange,
      selectedWorkItemTypeId = null,
      onWorkItemTypeChange,
      workItemTypeCount = 0,
      selectedFiles = [],
      onFilesChange,
      isSomeday = false,
      onSomedayChange,
      reminderOffsetMinutes = null,
      onReminderChange,
      recurrencePattern = null,
      onRecurrenceChange,
      enableNaturalLanguageDate = true,
      parsedCount = 0,
      defaultProjectId = null,
      extraChips,

      // Layout
      variant = 'inline',
      showDescription = true,
      showProject = false,
      outlined = true,
      placeholder,
      descriptionPlaceholder,
      icon = null,

      // Status mode
      statusMode = 'simple',

      // Class names
      className,
      inputClassName,
      controlsClassName,
    },
    ref,
  ) => {
    const { t } = useTranslation(['tasks', 'common'])
    const isMobile = useIsMobile()
    const canCreateTasks = useHasPermission(WORKSPACE_PERMISSIONS.CREATE_TASKS)
    const { isPersonal } = useWorkspaceMode()
    const { locale, is24Hour } = useLocale()

    const [internalNameValue, setInternalNameValue] = useState(nameValue ?? '')
    const [internalDescValue, setInternalDescValue] = useState(
      descriptionValue ?? '',
    )
    const containerRef = useRef<HTMLDivElement>(null)
    const [isLabelPickerOpen, setIsLabelPickerOpen] = useState(false)
    const [isPriorityPickerOpen, setIsPriorityPickerOpen] = useState(false)
    const [isStatusPickerOpen, setIsStatusPickerOpen] = useState(false)
    const [isAssigneePickerOpen, setIsAssigneePickerOpen] = useState(false)
    const [isProjectPickerOpen, setIsProjectPickerOpen] = useState(false)
    const [naturalDateSuggestion, setNaturalDateSuggestion] =
      useState<NaturalDateMatch | null>(null)
    const [dismissedMatchText, setDismissedMatchText] = useState<string | null>(
      null,
    )
    // Row variant: Tab opens the description line (design 25a).
    const [isRowExpanded, setIsRowExpanded] = useState(false)
    const descriptionEditorRef = useRef<Editor | null>(null)
    const pendingDescriptionFocusRef = useRef(false)
    const rowFileInputRef = useRef<HTMLInputElement>(null)
    const handleDescriptionEditorReady = useCallback((editor: Editor | null) => {
      descriptionEditorRef.current = editor
      if (editor && pendingDescriptionFocusRef.current) {
        pendingDescriptionFocusRef.current = false
        editor.commands.focus('end')
      }
    }, [])

    const subscription = useCurrentSubscription()

    // C1 (soft-enforce): disable the add-task CTA when the workspace is at its
    // task cap. Backend/MCP also hard-enforce; this is pre-flight UI feedback.
    // Unlimited plans never block.
    const taskUsage = useUsageLimitCheck('tasks')
    const tasksAtLimit =
      !taskUsage.isUnlimited &&
      (taskUsage.isOverLimit || taskUsage.currentUsage + 1 > taskUsage.limit)

    useEffect(() => {
      if (!import.meta.env.DEV) return
      if (!subscription) {
        console.log('No subscription for this workspace')
        return
      }
      console.log('Current subscription', subscription)
    }, [subscription])

    // Get localized placeholders
    const defaultPlaceholder = placeholder ?? t('tasks:addPlaceholder')
    const defaultDescPlaceholder =
      descriptionPlaceholder ?? t('tasks:descriptionPlaceholder')
    const createDisabledPlaceholder = t('tasks:createPermissionPlaceholder', {
      defaultValue: 'You do not have permission to create tasks',
    })
    const descriptionAriaLabel = t('tasks:aria.taskDescription')

    const assigneeShortcutKeys = useMemo(() => getPlatformShortcutKeys('U'), [])
    const labelShortcutKeys = useMemo(() => getPlatformShortcutKeys('L'), [])
    const projectShortcutKeys = useMemo(() => ['Shift', 'P'], [])
    const addMoreShortcutKeys = useMemo(() => getPlatformShortcutKeys('M'), [])

    const isControlled = onNameChange !== undefined
    const currentNameValue = isControlled
      ? (nameValue ?? '')
      : internalNameValue
    const currentDescValue =
      onDescriptionChange !== undefined
        ? (descriptionValue ?? '')
        : internalDescValue

    const handleNameChange = (value: string) => {
      if (value.length > 500) return

      // Detect a trailing natural-language date phrase and offer it as a
      // non-destructive suggestion. The user accepts via click; we never
      // mutate the title silently.
      if (enableNaturalLanguageDate && onDueDateChange) {
        const match = detectTrailingDate(value)
        if (match) {
          if (match.matchedText !== dismissedMatchText) {
            setNaturalDateSuggestion(match)
          } else {
            setNaturalDateSuggestion(null)
          }
        } else {
          setNaturalDateSuggestion(null)
          // The dismissed phrase is gone from the title — clear the
          // suppression so future matches of the same phrase suggest again.
          if (dismissedMatchText) setDismissedMatchText(null)
        }
      }

      if (isControlled) {
        onNameChange(value)
      } else {
        setInternalNameValue(value)
      }
    }

    const applyNaturalDateSuggestion = () => {
      if (!naturalDateSuggestion || !onDueDateChange) return
      if (isControlled) {
        onNameChange(naturalDateSuggestion.cleanedTitle)
      } else {
        setInternalNameValue(naturalDateSuggestion.cleanedTitle)
      }
      onDueDateChange(naturalDateSuggestion.date)
      if (isSomeday && onSomedayChange) onSomedayChange(false)
      setNaturalDateSuggestion(null)
      setDismissedMatchText(null)
    }

    const dismissNaturalDateSuggestion = () => {
      if (!naturalDateSuggestion) return
      setDismissedMatchText(naturalDateSuggestion.matchedText)
      setNaturalDateSuggestion(null)
    }

    const handleDescChange = (value: string) => {
      // Editor provides JSON string, no length limit here as it's rich content
      if (onDescriptionChange) {
        onDescriptionChange(value)
      } else {
        setInternalDescValue(value)
      }
    }

    const handleLabelSelection = (labelIds: Array<string>) => {
      if (!onLabelsChange) {
        return
      }

      onLabelsChange(labelIds)
    }

    const handleUploadClick = () => {
      return true
    }

    const handleFilesChange = (files: Array<File>) => {
      if (onFilesChange) {
        onFilesChange(files)
      }
    }

    // Drag-and-drop + paste-to-attach
    const [isDragging, setIsDragging] = useState(false)
    const [dropErrorMessage, setDropErrorMessage] = useState<string | null>(
      null,
    )
    const dragCounterRef = useRef(0)
    const canStageFiles = !!onFilesChange && canCreateTasks

    const isFileDrag = (event: React.DragEvent) => {
      const types = event.dataTransfer?.types
      if (!types) return false
      // DataTransferItemList has `contains`; in some browsers `types` is a
      // DOMStringList without `includes`. Normalize via Array.from.
      return Array.from(types).includes('Files')
    }

    const stageDroppedFiles = (incoming: Array<File>) => {
      if (!onFilesChange || incoming.length === 0) return
      const { validFiles, errors } = validateFiles(incoming, selectedFiles)
      if (errors.length > 0) setDropErrorMessage(errors.join('\n'))
      if (validFiles.length > 0) {
        onFilesChange([...selectedFiles, ...validFiles])
      }
    }

    const handleDragEnter = (event: React.DragEvent) => {
      if (!canStageFiles || !isFileDrag(event)) return
      event.preventDefault()
      dragCounterRef.current += 1
      setIsDragging(true)
    }

    const handleDragOver = (event: React.DragEvent) => {
      if (!canStageFiles || !isFileDrag(event)) return
      event.preventDefault()
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
    }

    const handleDragLeave = (event: React.DragEvent) => {
      if (!canStageFiles || !isFileDrag(event)) return
      event.preventDefault()
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1)
      if (dragCounterRef.current === 0) setIsDragging(false)
    }

    const handleDrop = (event: React.DragEvent) => {
      if (!canStageFiles || !isFileDrag(event)) return
      event.preventDefault()
      event.stopPropagation()
      dragCounterRef.current = 0
      setIsDragging(false)
      const files = Array.from(event.dataTransfer?.files ?? [])
      stageDroppedFiles(files)
    }

    // Capture-phase paste handler so we intercept image clipboard payloads
    // before TipTap's editor sees them. Text/HTML pastes fall through so
    // normal description editing keeps working.
    //
    // This is attached as a NATIVE listener on the container (see effect
    // below) rather than React's `onPasteCapture`. React delegates synthetic
    // events from the app root, so `onPasteCapture` never fires for pastes
    // that happen inside a portal — e.g. the quick-create task dialog renders
    // through a Radix portal in `document.body`, a sibling of the React root.
    // That gap is MOL-13: pasting an image in Create Task silently did
    // nothing because the handler never ran and ProseMirror dropped the file.
    // The "latest ref" pattern keeps current state visible to the native
    // listener, which is bound once.
    const handlePasteCaptureRef = useRef<(event: ClipboardEvent) => void>(
      () => {},
    )
    handlePasteCaptureRef.current = (event: ClipboardEvent) => {
      if (!canStageFiles) return
      const items = event.clipboardData?.items
      if (!items || items.length === 0) return
      const files: Array<File> = []
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item?.kind === 'file') {
          const file = item.getAsFile()
          if (file) files.push(file)
        }
      }
      if (files.length === 0) return
      event.preventDefault()
      event.stopPropagation()
      stageDroppedFiles(files)
    }

    useEffect(() => {
      const node = containerRef.current
      if (!node) return
      const listener = (event: ClipboardEvent) =>
        handlePasteCaptureRef.current(event)
      // Capture phase so we run before ProseMirror's paste handler on the
      // inner contenteditable.
      node.addEventListener('paste', listener, true)
      return () => node.removeEventListener('paste', listener, true)
    }, [variant])

    const renderDropOverlay = () =>
      isDragging ? (
        <div
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-primary bg-primary/10"
          aria-hidden="true"
        >
          <div className="flex items-center gap-2 rounded-md bg-card px-3 py-2 text-sm font-medium text-primary shadow-sm">
            <Upload className="size-4" />
            {t('tasks:attachments.dropToAttach', {
              defaultValue: 'Drop to attach',
            })}
          </div>
        </div>
      ) : null

    const renderDropErrorDialog = () => (
      <AlertDialog
        open={!!dropErrorMessage}
        onOpenChange={(open) => !open && setDropErrorMessage(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('tasks:attachments.cannotAddTitle', {
                defaultValue: 'Cannot Add Files',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {dropErrorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setDropErrorMessage(null)}>
              {t('common:actions.ok', { defaultValue: 'OK' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )

    // Get selected labels for display
    const selectedLabels = availableLabels.filter((label) =>
      selectedLabelIds.includes(label.id),
    )

    const isFocusWithinContainer = useCallback(() => {
      if (!containerRef.current) {
        return false
      }

      const activeElement = document.activeElement
      // Allow when no active element, body is focused (clicked neutral area), or focus is within container
      return (
        !activeElement ||
        activeElement === document.body ||
        containerRef.current.contains(activeElement)
      )
    }, [])

    useHotkeys(
      'p',
      (event) => {
        if (!isFocusWithinContainer()) {
          return
        }

        // Don't trigger when typing in text inputs
        const activeElement = document.activeElement
        if (
          activeElement instanceof HTMLInputElement ||
          activeElement instanceof HTMLTextAreaElement ||
          (activeElement instanceof HTMLElement &&
            activeElement.isContentEditable)
        ) {
          return
        }

        event.preventDefault()
        setIsPriorityPickerOpen(true)
      },
      { enableOnFormTags: true },
      [isFocusWithinContainer],
    )

    useHotkeys(
      'mod+s',
      (event) => {
        if (!isFocusWithinContainer()) {
          return
        }

        event.preventDefault()
        setIsStatusPickerOpen(true)
      },
      { enableOnFormTags: true },
      [isFocusWithinContainer],
    )

    useHotkeys(
      'mod+u',
      (event) => {
        if (!isFocusWithinContainer()) {
          return
        }
        event.preventDefault()
        setIsAssigneePickerOpen(true)
      },
      { enableOnFormTags: true },
      [isFocusWithinContainer],
    )

    useHotkeys(
      'mod+l',
      (event) => {
        if (!isFocusWithinContainer()) {
          return
        }
        event.preventDefault()
        setIsLabelPickerOpen(true)
      },
      { enableOnFormTags: true },
      [isFocusWithinContainer],
    )

    useHotkeys(
      'shift+p',
      (event) => {
        if (!isFocusWithinContainer()) {
          return
        }

        // Don't trigger when typing in text inputs
        const activeElement = document.activeElement
        if (
          activeElement instanceof HTMLInputElement ||
          activeElement instanceof HTMLTextAreaElement ||
          (activeElement instanceof HTMLElement &&
            activeElement.isContentEditable)
        ) {
          return
        }

        event.preventDefault()
        setIsProjectPickerOpen(true)
      },
      { enableOnFormTags: true },
      [isFocusWithinContainer],
    )

    useHotkeys(
      'mod+m',
      (event) => {
        if (!isFocusWithinContainer()) {
          return
        }
        if (!onAddMoreChange) {
          return
        }
        event.preventDefault()
        onAddMoreChange(!addMore)
      },
      { enableOnFormTags: true },
      [isFocusWithinContainer, onAddMoreChange, addMore],
    )

    const renderQuickActionsMenu = () => {
      const hasActiveOptions = !!(dueDate || startDate || isSomeday)

      // Color-coded display info; a range reads "Sep 28 → Oct 2" (design 31b).
      const displayInfo = getDueDateDisplayInfo(
        dueDate,
        startDate,
        isSomeday,
        isAllDay,
        { locale, is24Hour },
      )

      return (
        <div className="flex items-center">
          <DateDurationPicker
            startDate={startDate}
            dueDate={dueDate}
            onStartDateChange={onStartDateChange || (() => {})}
            onDueDateChange={onDueDateChange || (() => {})}
            isSomeday={isSomeday}
            onSomedayChange={onSomedayChange}
            isAllDay={isAllDay}
            onIsAllDayChange={onIsAllDayChange}
            reminderOffsetMinutes={reminderOffsetMinutes}
            onReminderChange={onReminderChange}
            recurrencePattern={recurrencePattern}
            onRecurrenceChange={onRecurrenceChange}
            trigger={
              <Button
                type="button"
                variant="ghost"
                className={cn(
                  'h-7 rounded-sm border border-input bg-card px-2 text-xs font-medium hover:border-border hover:bg-secondary text-foreground',
                  hasActiveOptions && 'border-border',
                )}
              >
                <CalendarIcon
                  className={cn('mr-1 size-3.5', displayInfo.colorClass)}
                />
                <span className="truncate max-w-[160px] sm:max-w-[220px]">
                  {displayInfo.label || t('tasks:actions.setDueDate')}
                </span>
              </Button>
            }
          />
        </div>
      )
    }

    const renderLabelsControl = () => {
      if (!onLabelsChange) {
        return null
      }

      return (
        <div className="flex items-center">
          <ResponsivePopover
            open={isLabelPickerOpen}
            onOpenChange={setIsLabelPickerOpen}
            trigger={
              <div
                title={t('common:shortcuts.labels', {
                  keys: labelShortcutKeys.join(' + '),
                })}
              >
                <TaskLabelsDisplay
                  labels={selectedLabels}
                  onClick={() => setIsLabelPickerOpen(true)}
                  size="sm"
                />
              </div>
            }
            contentClassName={pickerPopoverClassName}
            sideOffset={PICKER_SIDE_OFFSET}
            align="start"
          >
            <LabelSelectionPanel
              availableLabels={availableLabels}
              labelGroups={labelGroups}
              projects={projects}
              selectedLabelIds={selectedLabelIds}
              onSelectionChange={handleLabelSelection}
              projectId={selectedProjectId ?? null}
            />
          </ResponsivePopover>
        </div>
      )
    }

    const desktopControlButtonClass =
      'h-6 w-auto min-w-0 flex-none rounded-sm border border-input bg-card px-2 text-xs font-medium text-foreground hover:border-border hover:bg-secondary'
    const mobileControlButtonClass =
      'h-8 w-auto border-0 bg-transparent shadow-none px-0 text-sm font-medium text-foreground'

    const renderWorkItemTypeControl = (buttonClassName: string) =>
      !isPersonal && onWorkItemTypeChange && workItemTypeCount >= 1 ? (
        <WorkItemTypePicker
          currentTypeId={selectedWorkItemTypeId}
          onTypeChange={onWorkItemTypeChange}
          projectId={selectedProjectId}
          buttonClassName={buttonClassName}
        />
      ) : null

    const renderPriorityControl = (
      buttonClassName: string,
      fontSize?: 'xs',
    ) => (
      <PriorityComboBox
        selectedPriority={selectedPriority}
        onPriorityChange={onPriorityChange || (() => {})}
        buttonClassName={buttonClassName}
        fontSize={fontSize}
        popoverOpen={isPriorityPickerOpen}
        onPopoverOpenChange={setIsPriorityPickerOpen}
      />
    )

    const renderStatusControl = (buttonClassName: string, fontSize?: 'xs') =>
      statusMode === 'advanced' ? (
        <StatusComboBox
          statuses={statuses}
          selectedStatusId={selectedStatusId}
          onStatusChange={onStatusChange || (() => {})}
          buttonClassName={buttonClassName}
          fontSize={fontSize}
          popoverOpen={isStatusPickerOpen}
          onPopoverOpenChange={setIsStatusPickerOpen}
        />
      ) : null

    const renderAssigneeControl = (buttonClassName: string, fontSize?: 'xs') =>
      !isPersonal ? (
        <AssigneeComboBox
          projectId={selectedProjectId}
          selectedUserId={selectedUserId}
          onUserChange={onUserChange || (() => {})}
          allowSuperAgent={
            projects.find((p) => p.id === selectedProjectId)?.superAgentEnabled ?? false
          }
          buttonClassName={buttonClassName}
          fontSize={fontSize}
          popoverOpen={isAssigneePickerOpen}
          onPopoverOpenChange={setIsAssigneePickerOpen}
          triggerProps={
            fontSize === 'xs'
              ? {
                  title: `Assignee (${assigneeShortcutKeys.join(' + ')})`,
                }
              : undefined
          }
          searchShortcutHint={
            fontSize === 'xs' ? assigneeShortcutKeys : undefined
          }
        />
      ) : null

    const renderProjectControl = (
      buttonClassName: string,
      fontSize?: 'xs',
      triggerContent?: ReactNode,
    ) => (
      <ProjectComboBox
        triggerContent={triggerContent}
        projects={projects}
        selectedProjectId={selectedProjectId}
        onProjectChange={onProjectChange || (() => {})}
        buttonClassName={buttonClassName}
        fontSize={fontSize}
        popoverOpen={isProjectPickerOpen}
        onPopoverOpenChange={setIsProjectPickerOpen}
        triggerProps={
          fontSize === 'xs'
            ? {
                title: `Project (${projectShortcutKeys.join(' + ')})`,
              }
            : undefined
        }
        searchShortcutHint={fontSize === 'xs' ? projectShortcutKeys : undefined}
        isDisabled={disableProjectSelection}
      />
    )

    const renderControlRowItem = (content: ReactNode) =>
      content ? (
        <div className="flex items-center justify-between py-3">{content}</div>
      ) : null

    const renderMainControls = () => (
      <div
        className={cn(
          'flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground',
          controlsClassName,
        )}
      >
        {renderWorkItemTypeControl(desktopControlButtonClass)}
        {renderPriorityControl(desktopControlButtonClass, 'xs')}
        {renderStatusControl(desktopControlButtonClass, 'xs')}
        {renderAssigneeControl(desktopControlButtonClass, 'xs')}
        {renderLabelsControl()}
        {renderQuickActionsMenu()}
      </div>
    )

    const renderFooter = () => (
      <div className="flex items-center justify-between gap-2 border-t border-border pt-2 mt-2">
        {/* Left side: Project picker */}
        <div className="flex items-center gap-2">
          {showProject &&
            renderProjectControl(
              'h-auto border-0 bg-transparent shadow-none px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary',
              'xs',
            )}
        </div>

        {/* Right side: Action buttons */}
        {(onCreateClick || onCancelClick) && (
          <div className="flex items-center gap-3">
            {onAddMoreChange && (
              <div
                className="flex items-center gap-2"
                title={t('common:shortcuts.addMore', {
                  keys: addMoreShortcutKeys.join(' + '),
                  defaultValue: `Add More (${addMoreShortcutKeys.join(' + ')})`,
                })}
              >
                <Switch
                  id="add-more-switch"
                  checked={addMore}
                  onCheckedChange={onAddMoreChange}
                  className="h-[1.15rem] w-8"
                />
                <label
                  htmlFor="add-more-switch"
                  className="text-xs text-muted-foreground cursor-pointer select-none"
                >
                  {t('tasks:addMore')}
                </label>
              </div>
            )}
            {onCancelClick && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onCancelClick}
                className="h-7 px-3 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary"
              >
                {t('common:actions.cancel')}
              </Button>
            )}
            {onCreateClick && (
              <Button
                type="button"
                onClick={onCreateClick}
                disabled={!canCreateTasks || !currentNameValue.trim() || tasksAtLimit}
                title={tasksAtLimit ? t('tasks:taskLimitReached') : undefined}
                className="h-7 px-3 text-xs"
              >
                {t('common:actions.create')}
              </Button>
            )}
          </div>
        )}
      </div>
    )

    const renderMobileControls = () => (
      <div className="flex flex-col divide-y divide-border">
        {renderControlRowItem(
          renderWorkItemTypeControl(mobileControlButtonClass),
        )}
        {renderControlRowItem(renderPriorityControl(mobileControlButtonClass))}
        {renderControlRowItem(renderStatusControl(mobileControlButtonClass))}
        {renderControlRowItem(renderAssigneeControl(mobileControlButtonClass))}
        {renderControlRowItem(renderQuickActionsMenu())}
        {renderControlRowItem(onLabelsChange ? renderLabelsControl() : null)}
        {showProject &&
          onProjectChange &&
          renderControlRowItem(renderProjectControl(mobileControlButtonClass))}
      </div>
    )

    const renderMobileFooter = () => (
      <div className="flex flex-col gap-2 border-t border-border pt-3 mt-2">
        {onCreateClick && (
          <Button
            type="button"
            onClick={onCreateClick}
            disabled={!canCreateTasks || !currentNameValue.trim() || tasksAtLimit}
            title={tasksAtLimit ? t('tasks:taskLimitReached') : undefined}
            className="w-full h-10 text-sm"
          >
            {t('common:actions.create')}
          </Button>
        )}
        {onCancelClick && (
          <Button
            type="button"
            variant="ghost"
            onClick={onCancelClick}
            className="w-full h-10 text-sm text-muted-foreground"
          >
            {t('common:actions.cancel')}
          </Button>
        )}
      </div>
    )

    const renderNameInput = (
      inputBaseClassName: string,
      handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void = (
        event,
      ) => onKeyDown?.(event),
    ) => (
      <div className="flex items-center gap-2">
        {icon && <div className="shrink-0 text-muted-foreground/70">{icon}</div>}
        <input
          ref={ref}
          data-testid="task-add-input"
          type="text"
          placeholder={
            !canCreateTasks ? createDisabledPlaceholder : defaultPlaceholder
          }
          value={currentNameValue}
          onChange={(e) => handleNameChange(e.target.value)}
          onPaste={(e) => {
            if (!onPasteLines) return
            const lines = e.clipboardData
              .getData('text/plain')
              .split(/\r?\n/)
              .map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').trim())
              .filter(Boolean)
            if (lines.length < 2) return
            e.preventDefault()
            onPasteLines(lines)
          }}
          onKeyDown={handleKeyDown}
          maxLength={500}
          disabled={!canCreateTasks}
          className={cn(
            inputBaseClassName,
            !canCreateTasks && 'cursor-not-allowed opacity-50',
            inputClassName,
          )}
        />
      </div>
    )

    const renderDescriptionInput = ({
      wrapperClassName,
      namespace,
      contentEditableClassName,
      placeholderClassName,
      onEditorReady,
    }: {
      wrapperClassName: string
      namespace: string
      contentEditableClassName: string
      placeholderClassName: string
      onEditorReady?: (editor: Editor | null) => void
    }) =>
      showDescription ? (
        <div className={wrapperClassName}>
          <TiptapRichTextEditor
            namespace={namespace}
            initialContent={currentDescValue}
            editable
            placeholder={defaultDescPlaceholder}
            contentEditableClassName={contentEditableClassName}
            placeholderClassName={placeholderClassName}
            onContentChange={handleDescChange}
            ariaLabel={descriptionAriaLabel}
            toolbarItems={DESCRIPTION_TOOLBAR_ITEMS}
            className="min-h-[24px]"
            onEditorReady={onEditorReady}
          />
        </div>
      ) : null

    const renderAttachments = (wrapperClassName: string) =>
      onFilesChange ? (
        <div className={wrapperClassName}>
          <AttachmentPicker
            files={selectedFiles}
            onFilesChange={handleFilesChange}
            onClickUpload={handleUploadClick}
            disabled={false}
          />
        </div>
      ) : null

    const renderNaturalDateSuggestion = (wrapperClassName?: string) => {
      if (!naturalDateSuggestion) return null
      const applyLabel = t('tasks:naturalDate.apply', {
        defaultValue: 'Set due date · {{phrase}}',
        phrase: naturalDateSuggestion.matchedText,
      })
      const dismissLabel = t('common:actions.dismiss', {
        defaultValue: 'Dismiss',
      })
      return (
        <div
          className={cn(
            'mt-2 flex items-center gap-1.5 text-xs',
            wrapperClassName,
          )}
        >
          <button
            type="button"
            onClick={applyNaturalDateSuggestion}
            className="inline-flex items-center gap-1.5 rounded-sm border border-input bg-secondary/40 px-2 py-1 font-medium text-foreground transition-colors hover:border-border hover:bg-secondary"
          >
            <CalendarIcon className="size-3.5 text-muted-foreground" />
            <span>{applyLabel}</span>
          </button>
          <button
            type="button"
            onClick={dismissNaturalDateSuggestion}
            aria-label={dismissLabel}
            title={dismissLabel}
            className="inline-flex size-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </div>
      )
    }

    // Date chip (row and modal variants), with start date, reminder, repeat
    // and someday inside the picker.
    const hasDate = !!(dueDate || startDate || isSomeday)
    const dateInfo = getDueDateDisplayInfo(
      dueDate,
      startDate,
      isSomeday,
      isAllDay,
      { locale, is24Hour },
    )
    // "Tomorrow" reads better than "Sep 28" in a chip (design 25a).
    const dateChipLabel = (() => {
      if (isSomeday) return t('common:time.someday')
      if (!dueDate || startDate || isAllDay === false) return dateInfo.label
      const dayDiff = Math.round(
        (new Date(dueDate).setHours(0, 0, 0, 0) -
          new Date().setHours(0, 0, 0, 0)) /
          86_400_000,
      )
      if (dayDiff === 0) return t('common:time.today')
      if (dayDiff === 1) return t('common:time.tomorrow')
      return dateInfo.label
    })()

    // Row variant (design 25a): a 44px row shaped like the task it will
    // become. Properties sit on the right as ghost icons and turn into chips
    // once set; Tab opens the description, attachments and the Add button.
    if (variant === 'row') {
      const isExpanded = isRowExpanded || isMobile || selectedFiles.length > 0
      const hasName = currentNameValue.trim().length > 0

      const ghostClass =
        'inline-flex h-7 w-7 min-w-0 flex-none items-center justify-center rounded-md border-0 bg-transparent p-0 text-muted-foreground shadow-none hover:bg-accent hover:text-foreground focus:ring-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring *:text-muted-foreground hover:*:text-foreground'
      const chipTriggerClass =
        'inline-flex h-6 w-auto min-w-0 flex-none items-center gap-1 rounded-md border-0 bg-transparent py-0 pl-2 pr-0.5 text-xs font-medium text-foreground shadow-none hover:bg-transparent focus:ring-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      const chipClass =
        'inline-flex h-6 max-w-full items-center rounded-md bg-secondary text-xs font-medium text-foreground'

      const submitRow = (open: boolean) => {
        if (!canCreateTasks) return
        if (tasksAtLimit) {
          toast.error(t('tasks:taskLimitReached'))
          return
        }
        if (open && onCreateAndOpenClick) onCreateAndOpenClick()
        else onCreateClick?.()
      }

      const focusDescription = () => {
        if (!showDescription) return
        setIsRowExpanded(true)
        const editor = descriptionEditorRef.current
        if (editor && !editor.isDestroyed) {
          editor.commands.focus('end')
        } else {
          // The editor mounts with the expanded state; focus it once ready.
          pendingDescriptionFocusRef.current = true
        }
      }

      const handleRowNameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.nativeEvent.isComposing) return
        if (event.key === 'Enter') {
          event.preventDefault()
          event.stopPropagation()
          if (hasName) submitRow(event.shiftKey)
          return
        }
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          onCancelClick?.()
          return
        }
        if (
          event.key === 'Tab' &&
          !event.shiftKey &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey &&
          showDescription
        ) {
          event.preventDefault()
          focusDescription()
          return
        }
        onKeyDown?.(event)
      }

      // Keys from inside the description editor: Esc closes, Mod+Enter adds.
      const handleRowContainerKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement>,
      ) => {
        const target = event.target
        if (!(target instanceof HTMLElement) || !target.isContentEditable) return
        if (event.key === 'Escape' && !event.defaultPrevented) {
          event.preventDefault()
          onCancelClick?.()
        } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault()
          if (hasName) submitRow(event.shiftKey)
        }
      }

      const renderRowChip = (
        trigger: ReactNode,
        onRemove: () => void,
        name: string,
        tintClassName?: string,
      ) => (
        <span className={cn(chipClass, tintClassName)}>
          {trigger}
          <button
            type="button"
            onClick={onRemove}
            aria-label={t('tasks:quickAdd.remove', { name })}
            title={t('tasks:quickAdd.remove', { name })}
            className="mr-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded opacity-60 hover:bg-foreground/10 hover:opacity-100"
          >
            <X className="size-3" />
          </button>
        </span>
      )

      // Date, with start date, reminder, repeat and someday inside the picker.
      const clearDates = () => {
        onDueDateChange?.(undefined)
        onStartDateChange?.(undefined)
        if (isSomeday) onSomedayChange?.(false)
        onReminderChange?.(null)
        onRecurrenceChange?.(null)
      }
      const renderRowDate = () => {
        if (!onDueDateChange) return null
        const picker = (
          <DateDurationPicker
            startDate={startDate}
            dueDate={dueDate}
            onStartDateChange={onStartDateChange || (() => {})}
            onDueDateChange={onDueDateChange}
            isSomeday={isSomeday}
            onSomedayChange={onSomedayChange}
            isAllDay={isAllDay}
            onIsAllDayChange={onIsAllDayChange}
            reminderOffsetMinutes={reminderOffsetMinutes}
            onReminderChange={onReminderChange}
            recurrencePattern={recurrencePattern}
            onRecurrenceChange={onRecurrenceChange}
            trigger={
              hasDate ? (
                <button
                  type="button"
                  data-testid="quick-add-date"
                  className={chipTriggerClass}
                >
                  <CalendarIcon
                    className={cn(
                      'size-3.5',
                      dateInfo.colorClass || 'text-muted-foreground',
                    )}
                  />
                  <span className="max-w-[180px] truncate">
                    {dateChipLabel}
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="quick-add-date"
                  aria-label={t('tasks:quickAdd.date')}
                  title={t('tasks:quickAdd.date')}
                  className={ghostClass}
                >
                  <CalendarIcon className="size-4" />
                </button>
              )
            }
          />
        )
        return hasDate ? renderRowChip(picker, clearDates, dateChipLabel) : picker
      }

      const priorityLabels: Record<number, string> = {
        1: t('tasks:priority.low'),
        2: t('tasks:priority.medium'),
        3: t('tasks:priority.high'),
        4: t('tasks:priority.urgent'),
      }
      const priorityChipClass: Record<number, string> = {
        1: 'bg-blue-500/10 text-blue-700 dark:text-blue-300',
        2: 'bg-yellow-500/15 text-yellow-800 dark:text-yellow-300',
        3: 'bg-orange-500/12 text-orange-700 dark:text-orange-300',
        4: 'bg-red-500/12 text-red-700 dark:text-red-300',
      }
      const renderRowPriority = () => {
        if (!onPriorityChange) return null
        const label = priorityLabels[selectedPriority]
        const picker = (
          <PriorityComboBox
            variant="icon-only"
            selectedPriority={selectedPriority}
            onPriorityChange={onPriorityChange}
            popoverOpen={isPriorityPickerOpen}
            onPopoverOpenChange={setIsPriorityPickerOpen}
            buttonClassName={cn(
              label ? chipTriggerClass : ghostClass,
              label && 'text-inherit',
            )}
            triggerProps={{
              'aria-label': t('tasks:quickAdd.priority'),
              title: t('tasks:quickAdd.priority'),
            }}
            triggerContent={
              label ? (
                <>
                  <PriorityGlyph priority={selectedPriority} />
                  <span>{label}</span>
                </>
              ) : (
                <TbAntennaBars5 className="size-4" />
              )
            }
          />
        )
        return label
          ? renderRowChip(
              picker,
              () => onPriorityChange(0),
              label,
              priorityChipClass[selectedPriority],
            )
          : picker
      }

      // null = the default (you), '' = explicitly nobody.
      const renderRowAssignee = () => {
        if (isPersonal || !onUserChange) return null
        const mode =
          selectedUserId === null ? 'ghost' : selectedUserId === '' ? 'none' : 'user'
        const noneLabel = t('tasks:quickAdd.noAssignee')
        const picker = (
          <AssigneeComboBox
            projectId={selectedProjectId}
            key={mode}
            selectedUserId={selectedUserId}
            onUserChange={onUserChange}
            Icon={UserRound}
            placeholderText={mode === 'none' ? noneLabel : ''}
            noneLabel={noneLabel}
            allowSuperAgent={
              projects.find((p) => p.id === selectedProjectId)
                ?.superAgentEnabled ?? false
            }
            buttonClassName={mode === 'ghost' ? ghostClass : chipTriggerClass}
            popoverOpen={isAssigneePickerOpen}
            onPopoverOpenChange={setIsAssigneePickerOpen}
            triggerProps={{
              'aria-label': t('tasks:quickAdd.assignee'),
              title: t('tasks:quickAdd.assignee'),
            }}
            searchShortcutHint={assigneeShortcutKeys}
          />
        )
        return mode === 'ghost'
          ? picker
          : renderRowChip(
              picker,
              () => onUserChange(null),
              t('tasks:quickAdd.assignee'),
            )
      }

      const selectedProject = projects.find((p) => p.id === selectedProjectId)
      const projectChanged = selectedProjectId !== defaultProjectId
      const renderRowProject = () => {
        if (!onProjectChange || disableProjectSelection) return null
        const name = selectedProject?.name ?? t('tasks:quickAdd.inbox')
        const picker = renderProjectControl(
          projectChanged ? chipTriggerClass : ghostClass,
          'xs',
          projectChanged ? (
            <>
              {selectedProject ? (
                <Folder className="size-3.5 text-muted-foreground" />
              ) : (
                <Inbox className="size-3.5 text-muted-foreground" />
              )}
              <span className="max-w-[140px] truncate">{name}</span>
            </>
          ) : (
            <Folder
              className="size-4"
              aria-label={t('tasks:quickAdd.project')}
            />
          ),
        )
        return projectChanged
          ? renderRowChip(
              picker,
              () => onProjectChange(defaultProjectId),
              name,
            )
          : picker
      }

      const renderRowLabels = () => {
        if (!onLabelsChange) return null
        const first = selectedLabels[0]
        const trigger = first ? (
          <button type="button" className={chipTriggerClass}>
            <span
              className="size-2 shrink-0 rounded-full"
              style={{ backgroundColor: first.color }}
            />
            <span className="max-w-[120px] truncate">{first.name}</span>
            {selectedLabels.length > 1 && (
              <span className="text-muted-foreground">
                +{selectedLabels.length - 1}
              </span>
            )}
          </button>
        ) : (
          <button
            type="button"
            aria-label={t('tasks:quickAdd.labels')}
            title={t('common:shortcuts.labels', {
              keys: labelShortcutKeys.join(' + '),
            })}
            className={ghostClass}
          >
            <Tag className="size-4" />
          </button>
        )
        const popover = (
          <ResponsivePopover
            open={isLabelPickerOpen}
            onOpenChange={setIsLabelPickerOpen}
            trigger={trigger}
            contentClassName={pickerPopoverClassName}
            sideOffset={PICKER_SIDE_OFFSET}
            align="end"
          >
            <LabelSelectionPanel
              availableLabels={availableLabels}
              labelGroups={labelGroups}
              projects={projects}
              selectedLabelIds={selectedLabelIds}
              onSelectionChange={handleLabelSelection}
              projectId={selectedProjectId ?? null}
            />
          </ResponsivePopover>
        )
        return first
          ? renderRowChip(
              popover,
              () => onLabelsChange([]),
              t('tasks:quickAdd.labels'),
            )
          : popover
      }

      const selectedStatus = statuses.find((s) => s.id === selectedStatusId)
      const statusSlot =
        statusMode === 'advanced' && onStatusChange && statuses.length > 0 ? (
          <StatusComboBox
            variant="icon-only"
            statuses={statuses}
            selectedStatusId={selectedStatusId}
            onStatusChange={onStatusChange}
            buttonClassName="h-6 w-6 focus:ring-0 focus-visible:ring-2 focus-visible:ring-ring"
            popoverOpen={isStatusPickerOpen}
            onPopoverOpenChange={setIsStatusPickerOpen}
            triggerProps={{ 'aria-label': t('tasks:quickAdd.status') }}
          />
        ) : (
          <span className="flex size-6 shrink-0 items-center justify-center" aria-hidden>
            <StatusIcon
              progressType={selectedStatus?.progressType ?? 'BL'}
              statusId={selectedStatus?.id}
            />
          </span>
        )

      const handleRowFilesPicked = (event: React.ChangeEvent<HTMLInputElement>) => {
        stageDroppedFiles(Array.from(event.target.files ?? []))
        event.target.value = ''
      }

      const keyHint = (keys: string, label: string, className?: string) => (
        <span className={cn('inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap phone:hidden', className)}>
          <Kbd className="h-4 min-w-4 rounded-[4px] border border-border bg-card px-1 font-mono text-[10px] text-muted-foreground">
            {keys}
          </Kbd>
          <span>{label}</span>
        </span>
      )

      return (
        <>
          <div className={cn('@container/qa w-full', className)}>
            <div
              ref={containerRef}
              data-testid="quick-add-row"
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onKeyDown={handleRowContainerKeyDown}
              className="relative rounded-lg border border-foreground/55 bg-card shadow-[0_0_0_3px_color-mix(in_oklab,var(--brand)_13%,transparent)]"
            >
              <div className="flex min-h-11 flex-wrap items-center gap-x-2 gap-y-0.5 py-1.5 pl-2.5 pr-2">
                {statusSlot}
                <div className="min-w-[12rem] flex-1">
                  {renderNameInput(
                    'w-full min-w-0 bg-transparent py-1 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none md:text-sm',
                    handleRowNameKeyDown,
                  )}
                </div>
                <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-1">
                  {renderRowDate()}
                  {renderRowPriority()}
                  {renderRowAssignee()}
                  {renderRowProject()}
                  {renderRowLabels()}
                </div>
              </div>

              {isExpanded && (
                <div className="pb-2 pl-[2.625rem] pr-2">
                  {renderDescriptionInput({
                    wrapperClassName: 'pb-1',
                    namespace: 'add-task-description-row',
                    contentEditableClassName:
                      'text-[13.5px] min-h-[22px] py-0',
                    placeholderClassName: 'text-[13.5px] top-0',
                    onEditorReady: handleDescriptionEditorReady,
                  })}

                  {selectedFiles.length > 0 && (
                    <div className="flex flex-wrap gap-1 py-1">
                      {selectedFiles.map((file, index) => (
                        <span
                          key={`${file.name}-${index}`}
                          className={cn(chipClass, 'pl-2')}
                        >
                          <Paperclip className="mr-1 size-3 text-muted-foreground" />
                          <span className="max-w-[180px] truncate">{file.name}</span>
                          <button
                            type="button"
                            onClick={() =>
                              handleFilesChange(
                                selectedFiles.filter((_, i) => i !== index),
                              )
                            }
                            aria-label={t('tasks:quickAdd.remove', { name: file.name })}
                            className="mx-0.5 inline-flex size-5 items-center justify-center rounded opacity-60 hover:bg-foreground/10 hover:opacity-100"
                          >
                            <X className="size-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {onFilesChange && (
                      <>
                        <input
                          ref={rowFileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          accept={FILE_INPUT_ACCEPT}
                          onChange={handleRowFilesPicked}
                          aria-label={t('tasks:aria.selectFile')}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => rowFileInputRef.current?.click()}
                          disabled={!canStageFiles}
                          className="h-7 gap-1.5 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                        >
                          <Paperclip className="size-3.5" />
                          {t('tasks:quickAdd.attach')}
                        </Button>
                      </>
                    )}
                    {renderWorkItemTypeControl(
                      'h-7 border-0 bg-transparent px-1.5 text-xs shadow-none hover:bg-accent',
                    )}
                    <div className="ml-auto flex items-center gap-1.5">
                      {showProject &&
                        renderProjectControl(
                          'h-7 border-0 bg-transparent px-2 text-xs font-medium text-muted-foreground shadow-none hover:bg-accent',
                          'xs',
                          <span
                            className="flex items-center gap-1.5"
                            title={t('tasks:quickAdd.savesTo')}
                          >
                            {selectedProject ? (
                              <Folder className="size-3.5" />
                            ) : (
                              <Inbox className="size-3.5" />
                            )}
                            <span className="max-w-[160px] truncate">
                              {selectedProject?.name ?? t('tasks:quickAdd.inbox')}
                            </span>
                          </span>,
                        )}
                      <Button
                        type="button"
                        size="sm"
                        data-testid="task-add-submit"
                        onClick={() => submitRow(false)}
                        disabled={!canCreateTasks}
                        className="h-7 px-3 text-xs"
                      >
                        {t('tasks:quickAdd.addTask')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {renderDropOverlay()}
            </div>

            {!isMobile && (
              <div className="mt-1.5 flex h-7 items-center gap-3 overflow-hidden rounded-md bg-secondary/60 pl-3 pr-3 text-[11.5px] text-muted-foreground @xl/qa:gap-4 @xl/qa:pl-11">
                {keyHint('↵', t('tasks:quickAdd.hint.addKeepGoing'))}
                {keyHint('⇧↵', t('tasks:quickAdd.hint.addOpen'))}
                {showDescription &&
                  keyHint(
                    'Tab',
                    t('tasks:quickAdd.hint.description'),
                    'hidden @xl/qa:inline-flex',
                  )}
                <span className="ml-auto flex shrink-0 items-center gap-3 @xl/qa:gap-4">
                  {parsedCount > 0 && (
                    <span
                      className="whitespace-nowrap font-medium text-emerald-700 dark:text-emerald-400"
                      data-testid="quick-add-parsed"
                    >
                      {t('tasks:quickAdd.parsed', { count: parsedCount })}
                    </span>
                  )}
                  {keyHint('Esc', t('tasks:quickAdd.hint.close'))}
                </span>
              </div>
            )}
          </div>
          {renderDropErrorDialog()}
        </>
      )
    }

    // Modal variant (design 29a): the global create dialog's body. A 19px
    // title, the description, labelled property chips (ghosts until set),
    // then attach, token hints, Create more and a ready Create button.
    if (variant === 'modal') {
      const hasName = currentNameValue.trim().length > 0
      const submitModal = () => {
        if (!canCreateTasks || !hasName) return
        if (tasksAtLimit) {
          toast.error(t('tasks:taskLimitReached'))
          return
        }
        onCreateClick?.()
      }

      // Mod+Enter creates from anywhere in the dialog, description included.
      const handleModalKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault()
          event.stopPropagation()
          submitModal()
        }
      }

      const selectedStatus = statuses.find((s) => s.id === selectedStatusId)
      const priorityLabels: Record<number, string> = {
        1: t('tasks:priority.low'),
        2: t('tasks:priority.medium'),
        3: t('tasks:priority.high'),
        4: t('tasks:priority.urgent'),
      }
      const priorityLabel = priorityLabels[selectedPriority]
      const firstLabel = selectedLabels[0]

      const handleModalFilesPicked = (event: React.ChangeEvent<HTMLInputElement>) => {
        stageDroppedFiles(Array.from(event.target.files ?? []))
        event.target.value = ''
      }

      const hintKey = (key: string, label: string) => (
        <span className="inline-flex items-center gap-1.5 phone:hidden">
          <Kbd className="h-4 min-w-4 rounded-[4px] border border-b-2 border-input bg-card px-1 font-mono text-[10.5px] text-muted-foreground">
            {key}
          </Kbd>
          {label}
        </span>
      )

      return (
        <>
          <div
            ref={containerRef}
            data-testid="quick-create-body"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onKeyDown={handleModalKeyDown}
            className={cn('relative flex min-h-0 flex-col', className)}
          >
            <div className="flex flex-col gap-2 px-[18px] pb-1.5 pt-4">
              {renderNameInput(
                'w-full min-w-0 bg-transparent text-[19px] font-semibold leading-[1.35] tracking-[-0.01em] text-foreground placeholder:font-semibold placeholder:text-muted-foreground/70 focus-visible:outline-none',
              )}
              {renderDescriptionInput({
                wrapperClassName: 'min-h-[22px]',
                namespace: 'add-task-description-modal',
                contentEditableClassName: cn(
                  'text-[14px] min-h-[22px] py-0',
                  inputClassName,
                ),
                placeholderClassName: 'text-[14px] top-0',
              })}
              {selectedFiles.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {selectedFiles.map((file, index) => (
                    <span
                      key={`${file.name}-${index}`}
                      className="inline-flex h-6 max-w-full items-center rounded-md bg-secondary pl-2 text-xs font-medium text-foreground"
                    >
                      <Paperclip className="mr-1 size-3 text-muted-foreground" />
                      <span className="max-w-[180px] truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() =>
                          handleFilesChange(
                            selectedFiles.filter((_, i) => i !== index),
                          )
                        }
                        aria-label={t('tasks:quickAdd.remove', { name: file.name })}
                        className="mx-0.5 inline-flex size-5 items-center justify-center rounded opacity-60 hover:bg-foreground/10 hover:opacity-100"
                      >
                        <X className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div
              className={cn(
                'flex flex-wrap gap-1.5 px-[18px] pb-4 pt-2.5',
                controlsClassName,
              )}
              data-testid="quick-create-chips"
            >
              {statusMode === 'advanced' && onStatusChange && statuses.length > 0 && (
                <StatusComboBox
                  statuses={statuses}
                  selectedStatusId={selectedStatusId}
                  onStatusChange={onStatusChange}
                  placeholderText={t('common:globalCreate.chips.status')}
                  buttonClassName={createChipClass(!!selectedStatus)}
                  popoverOpen={isStatusPickerOpen}
                  onPopoverOpenChange={setIsStatusPickerOpen}
                />
              )}
              {onPriorityChange && (
                <PriorityComboBox
                  variant="icon-only"
                  selectedPriority={selectedPriority}
                  onPriorityChange={onPriorityChange}
                  popoverOpen={isPriorityPickerOpen}
                  onPopoverOpenChange={setIsPriorityPickerOpen}
                  buttonClassName={createChipClass(!!priorityLabel)}
                  triggerProps={{ title: t('common:globalCreate.chips.priority') }}
                  triggerContent={
                    priorityLabel ? (
                      <>
                        <PriorityGlyph priority={selectedPriority} />
                        <span>{priorityLabel}</span>
                      </>
                    ) : (
                      <>
                        <TbAntennaBars5 className="size-3.5" />
                        <span>{t('common:globalCreate.chips.priority')}</span>
                      </>
                    )
                  }
                />
              )}
              {!isPersonal && onUserChange && (
                <AssigneeComboBox
                  projectId={selectedProjectId}
                  key={selectedUserId ? 'set' : 'unset'}
                  selectedUserId={selectedUserId}
                  onUserChange={onUserChange}
                  Icon={UserRound}
                  placeholderText={t('common:globalCreate.chips.assignee')}
                  allowSuperAgent={
                    projects.find((p) => p.id === selectedProjectId)
                      ?.superAgentEnabled ?? false
                  }
                  buttonClassName={createChipClass(!!selectedUserId)}
                  popoverOpen={isAssigneePickerOpen}
                  onPopoverOpenChange={setIsAssigneePickerOpen}
                  triggerProps={{
                    title: `${t('common:globalCreate.chips.assignee')} (${assigneeShortcutKeys.join(' + ')})`,
                  }}
                  searchShortcutHint={assigneeShortcutKeys}
                />
              )}
              {onDueDateChange && (
                <DateDurationPicker
                  startDate={startDate}
                  dueDate={dueDate}
                  onStartDateChange={onStartDateChange || (() => {})}
                  onDueDateChange={onDueDateChange}
                  isSomeday={isSomeday}
                  onSomedayChange={onSomedayChange}
                  isAllDay={isAllDay}
                  onIsAllDayChange={onIsAllDayChange}
                  reminderOffsetMinutes={reminderOffsetMinutes}
                  onReminderChange={onReminderChange}
                  recurrencePattern={recurrencePattern}
                  onRecurrenceChange={onRecurrenceChange}
                  trigger={
                    <button
                      type="button"
                      data-testid="quick-create-date"
                      className={createChipClass(hasDate)}
                    >
                      <CalendarIcon
                        className={cn(
                          'size-3.5',
                          hasDate && (dateInfo.colorClass || 'text-muted-foreground'),
                        )}
                      />
                      <span className="max-w-[180px] truncate">
                        {hasDate ? dateChipLabel : t('common:globalCreate.chips.due')}
                      </span>
                    </button>
                  }
                />
              )}
              {onLabelsChange && (
                <ResponsivePopover
                  open={isLabelPickerOpen}
                  onOpenChange={setIsLabelPickerOpen}
                  trigger={
                    <button
                      type="button"
                      className={createChipClass(!!firstLabel)}
                      title={t('common:shortcuts.labels', {
                        keys: labelShortcutKeys.join(' + '),
                      })}
                    >
                      {firstLabel ? (
                        <>
                          <span
                            className="size-[7px] shrink-0 rounded-full"
                            style={{ backgroundColor: firstLabel.color }}
                          />
                          <span className="max-w-[120px] truncate">{firstLabel.name}</span>
                          {selectedLabels.length > 1 && (
                            <span className="text-muted-foreground">
                              +{selectedLabels.length - 1}
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <Tag className="size-3.5" />
                          <span>{t('common:globalCreate.chips.labels')}</span>
                        </>
                      )}
                    </button>
                  }
                  contentClassName={pickerPopoverClassName}
            sideOffset={PICKER_SIDE_OFFSET}
                  align="start"
                >
                  <LabelSelectionPanel
                    availableLabels={availableLabels}
                    labelGroups={labelGroups}
                    projects={projects}
                    selectedLabelIds={selectedLabelIds}
                    onSelectionChange={handleLabelSelection}
                    projectId={selectedProjectId ?? null}
                  />
                </ResponsivePopover>
              )}
              {extraChips}
              {renderWorkItemTypeControl(createChipClass(!!selectedWorkItemTypeId))}
            </div>

            <div className="flex items-center gap-2.5 border-t border-border/70 bg-chrome py-2.5 pl-3 pr-3 text-[12.5px] text-muted-foreground sm:pl-4">
              {onFilesChange && (
                <>
                  <input
                    ref={rowFileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    accept={FILE_INPUT_ACCEPT}
                    onChange={handleModalFilesPicked}
                    aria-label={t('tasks:aria.selectFile')}
                  />
                  <button
                    type="button"
                    onClick={() => rowFileInputRef.current?.click()}
                    disabled={!canStageFiles}
                    aria-label={t('common:globalCreate.attach')}
                    title={t('common:globalCreate.attach')}
                    className="inline-flex size-7 shrink-0 items-center justify-center rounded-[7px] hover:bg-accent hover:text-foreground disabled:opacity-50"
                  >
                    <Paperclip className="size-3.5" />
                  </button>
                </>
              )}
              {!isMobile && (
                <span className="hidden items-center gap-2 whitespace-nowrap sm:inline-flex">
                  {!isPersonal && hintKey('@', t('common:globalCreate.hints.person'))}
                  {hintKey('!', t('common:globalCreate.hints.priority'))}
                  {hintKey('#', t('common:globalCreate.hints.label'))}
                </span>
              )}
              <span className="flex-1" />
              {onAddMoreChange && (
                <label
                  className="flex cursor-pointer select-none items-center gap-2 whitespace-nowrap"
                  title={t('common:shortcuts.addMore', {
                    keys: addMoreShortcutKeys.join(' + '),
                    defaultValue: `Add More (${addMoreShortcutKeys.join(' + ')})`,
                  })}
                >
                  <Switch
                    checked={addMore}
                    onCheckedChange={onAddMoreChange}
                    className="h-4 w-7"
                  />
                  {t('common:globalCreate.createMore')}
                </label>
              )}
              <Button
                type="button"
                data-testid="task-add-submit"
                onClick={submitModal}
                disabled={!canCreateTasks || tasksAtLimit}
                title={tasksAtLimit ? t('tasks:taskLimitReached') : undefined}
                className="h-[30px] gap-2 rounded-lg pl-3 pr-2 text-[12.5px] font-medium"
              >
                {t('common:globalCreate.create.task')}
                <span className="font-mono text-[10.5px] opacity-60 phone:hidden">
                  {getPlatformShortcutKeys('↵').join('')}
                </span>
              </Button>
            </div>

            {renderDropOverlay()}
          </div>
          {renderDropErrorDialog()}
        </>
      )
    }

    // Full variant with create/cancel buttons
    if (variant === 'full') {
      return (
        <>
          <div
            ref={containerRef}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              'relative rounded-sm border border-border bg-card p-2 md:p-2 shadow-sm overflow-hidden',
              isMobile && 'p-3',
              className,
            )}
          >
            {renderNameInput(
              'flex-1 bg-transparent text-base md:text-sm placeholder:text-muted-foreground focus-visible:outline-none',
            )}

            {renderNaturalDateSuggestion(icon && !isMobile ? 'ml-6' : '')}

            {renderDescriptionInput({
              wrapperClassName: cn('mt-2', icon && !isMobile ? 'ml-6' : ''),
              namespace: 'add-task-description',
              contentEditableClassName:
                'text-base md:text-sm min-h-[24px] py-0',
              placeholderClassName: 'text-base md:text-sm top-0',
            })}

            {renderAttachments('mt-2 w-full min-w-0 overflow-hidden')}

            {/* Main controls row */}
            <div className="mt-2 min-w-0">
              {isMobile ? renderMobileControls() : renderMainControls()}
            </div>

            {/* Footer */}
            {isMobile
              ? (onCreateClick || onCancelClick) && renderMobileFooter()
              : (showProject || onCreateClick || onCancelClick) &&
                renderFooter()}

            {renderDropOverlay()}
          </div>
          {renderDropErrorDialog()}
        </>
      )
    }

    // Inline variant (default - similar to current AddTaskInput)
    return (
      <>
        <div
          ref={containerRef}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'relative rounded-md bg-card',
            outlined && 'border border-border shadow-sm',
            className,
          )}
        >
          <div className="p-3">
            {renderNameInput(
              'flex-1 bg-transparent py-1 text-base placeholder:text-muted-foreground focus-visible:outline-none md:text-sm',
            )}

            {renderNaturalDateSuggestion(icon ? 'pl-6' : '')}

            {renderDescriptionInput({
              wrapperClassName: cn('w-full mt-2', icon ? 'pl-6' : ''),
              namespace: 'add-task-description-inline',
              contentEditableClassName: 'text-sm min-h-[24px] py-0',
              placeholderClassName: 'text-sm top-0',
            })}

            {renderAttachments('mt-2 w-full')}

            {/* Main controls row (Priority, Status, Date, Someday) */}
            <div className="mt-2">{renderMainControls()}</div>

            {/* Footer with Project picker (left) and optional action buttons (right) */}
            {(showProject || onCreateClick || onCancelClick) && renderFooter()}
          </div>
          {renderDropOverlay()}
        </div>
        {renderDropErrorDialog()}
      </>
    )
  },
)

AddTaskInput.displayName = 'AddTaskInput'
